// ═══════════════════════════════════════════════════════════
// Audit Log API
// ═══════════════════════════════════════════════════════════

import { queryRecords, createRecord } from './client';
import { TABLES } from './tables';
import { FIELDS } from './fields';

export interface AuditEntry {
  recordId:   number;
  requestId?: number;
  userId?:    string;
  userEmail:  string;
  action:     string;
  details?:   string;
  ipAddress?: string;
  timestamp:  string;
}

const F = FIELDS.auditLog;

function rowToEntry(row: Record<string, unknown>): AuditEntry {
  return {
    recordId:   row['record_id'] as number,
    requestId:  row[F.requestId] as number | undefined,
    userId:     row[F.userId] as string | undefined,
    userEmail:  String(row[F.userEmail] ?? ''),
    action:     String(row[F.action] ?? ''),
    details:    row[F.details] as string | undefined,
    ipAddress:  row[F.ipAddress] as string | undefined,
    timestamp:  String(row[F.timestamp] ?? ''),
  };
}

export async function listAuditLog(requestRecordId: number): Promise<AuditEntry[]> {
  const { records } = await queryRecords(TABLES.auditLog, {
    filters: [{ field: F.requestId, operator: 'eq', value: requestRecordId }],
    sort:    { field: F.timestamp, direction: 'desc' },
    limit:   500,
  });
  return (records as Record<string, unknown>[]).map(rowToEntry);
}

export interface CreateAuditInput {
  requestId?:  number;
  userId?:     string;
  userEmail?:  string;
  action:      string;
  details?:    string;
  ipAddress?:  string;
  anonymous?:  boolean;
}

export async function createAuditEntry(input: CreateAuditInput): Promise<void> {
  try {
    await createRecord(TABLES.auditLog, {
      [F.requestId]:  input.requestId ?? null,
      [F.userId]:     input.userId ?? '',
      [F.userEmail]:  input.userEmail ?? '',
      [F.action]:     input.action,
      [F.details]:    input.details ?? '',
      [F.ipAddress]:  input.ipAddress ?? '',
      [F.timestamp]:  new Date().toISOString(),
    }, { anonymous: input.anonymous });
  } catch { /* audit failures must never block main flow */ }
}

// Human-readable action labels
export const ACTION_LABELS: Record<string, string> = {
  'request.created':       'Request submitted',
  'request.updated':       'Request updated',
  'request.acknowledged':  'Request acknowledged',
  'request.assigned':      'Request assigned',
  'request.closed':        'Request closed',
  'clock.paused':          'Statutory clock paused',
  'clock.resumed':         'Statutory clock resumed',
  'clock.extended':        'Extension granted',
  'task.created':          'Task created',
  'task.updated':          'Task updated',
  'task.completed':        'Task completed',
  'document.uploaded':     'Document uploaded',
  'document.classified':   'Document classified',
  'document.released':     'Document released',
  'redaction.created':     'Redaction added',
  'redaction.approved':    'Redaction approved',
  'redaction.rejected':    'Redaction rejected',
  'correspondence.sent':   'Correspondence sent',
  'correspondence.received':'Correspondence received',
  'invoice.created':       'Invoice created',
  'invoice.sent':          'Invoice sent',
  'invoice.paid':          'Payment received',
};
